<template>
  <v-card outlined class="firmware-update-card mb-4">
    <v-card-text>
      <div class="d-flex align-center flex-wrap" style="gap: 8px">
        <div class="text-subtitle-1 font-weight-medium">
          {{ $t('firmware.engine.title', {version: update.targetVersion}) }}
        </div>
        <v-chip x-small :color="phaseColor" text-color="white">{{ $t('firmware.engine.phases.' + update.phase) }}</v-chip>
        <v-chip v-if="update.forced" x-small outlined color="orange">{{ $t('firmware.engine.forced') }}</v-chip>
        <v-spacer />
        <v-btn v-if="update.open && canDeploy" small text color="error" @click="cancelDialog = true">
          {{ $t('firmware.engine.cancel') }}
        </v-btn>
        <!-- Échouée ou arrêtée : la même cible, avec le même motif (le chemin repart de la version actuelle de la borne). -->
        <v-btn v-if="canRetry" small outlined color="primary" :loading="retrying" @click="retry">
          <v-icon small left>mdi-restart</v-icon>{{ $t('firmware.engine.retry') }}
        </v-btn>
      </div>
      <div class="text-caption text--secondary mb-3">
        {{ $t('firmware.engine.started-by', {date: formatDate(update.createdAt), by: update.requestedByName || '-'}) }}
        <template v-if="update.finishedAt"> · {{ $t('firmware.engine.finished-at', {date: formatDate(update.finishedAt)}) }}</template>
        <template v-if="update.reason"> · {{ $t('firmware.engine.because', {reason: update.reason}) }}</template>
      </div>

      <!-- Le chemin, marche par marche. -->
      <div class="d-flex align-center flex-wrap steps" style="gap: 4px">
        <template v-for="(step, index) in update.steps">
          <v-icon v-if="index > 0" :key="'arrow-' + index" small>mdi-arrow-right</v-icon>
          <v-chip
              :key="'step-' + index"
              small
              :color="stepColor(step, index)"
              :text-color="stepState(step) === 'pending' ? undefined : 'white'"
              :outlined="stepState(step) === 'pending'"
          >
            <v-icon v-if="stepState(step) === 'succeeded'" x-small left>mdi-check</v-icon>
            <v-icon v-else-if="stepState(step) === 'failed'" x-small left>mdi-close</v-icon>
            <v-icon v-else-if="stepState(step) === 'stopped'" x-small left>mdi-stop</v-icon>
            <v-progress-circular v-else-if="stepState(step) === 'running'" indeterminate size="10" width="2" class="mr-1" />
            {{ step.version }}
            <template v-if="stepState(step) === 'running' && step.lastStatus"> · {{ statusLabel(step.lastStatus) }}</template>
          </v-chip>
        </template>
      </div>

      <v-alert v-if="update.phase === 'waiting' && update.waitingReason" type="info" text dense class="mt-3 mb-0">
        {{ $t('firmware.engine.waiting.' + update.waitingReason) }}
      </v-alert>
      <!-- Le fichier est parti pendant une charge : la borne télécharge, puis attend la fin de la charge pour installer. -->
      <v-alert v-else-if="update.phase === 'transfer' && update.waitingReason === 'charging'" type="info" text dense class="mt-3 mb-0">
        {{ $t('firmware.engine.transfer-charging.' + (currentStep && currentStep.lastStatus === 'Downloaded' ? 'downloaded' : 'downloading')) }}
      </v-alert>
      <v-alert v-else-if="update.open" type="info" text dense class="mt-3 mb-0">
        {{ $t('firmware.engine.doing.' + update.phase, {step: currentStep ? currentStep.version : ''}) }}
      </v-alert>
      <v-alert v-if="update.phase === 'failed'" type="error" text dense class="mt-3 mb-0">
        {{ $t('firmware.engine.failures.' + update.failureCode, {detail: update.failureDetail || ''}) }}
        <!-- La borne a redémarré dans une autre version : si c'est bien ce firmware, sa version annoncée est à corriger. -->
        <div v-if="update.failureCode === 'wrong-version' && update.failureDetail && currentStep && canEditCatalogue" class="mt-2">
          <v-btn x-small outlined color="error" :to="{name: 'firmware.edit', params: {id: currentStep.versionId}, query: {rename: update.failureDetail}}">
            {{ $t('firmware.engine.rename-to', {version: update.failureDetail}) }}
          </v-btn>
        </div>
      </v-alert>
      <v-alert v-if="update.phase === 'cancelled'" type="warning" text dense class="mt-3 mb-0">
        {{ $t('firmware.engine.cancelled', {by: update.cancelledBy || '-'}) }}<template v-if="update.cancelReason"> — {{ update.cancelReason }}</template>
      </v-alert>

      <!-- La photo de configuration : avant, puis comparée après. -->
      <div v-if="update.phase === 'succeeded'" class="mt-3">
        <div v-if="update.configurationDifferences === null" class="text-caption text--secondary">
          {{ $t('firmware.engine.configuration-missing') }}
        </div>
        <div v-else-if="!update.configurationDifferences.length" class="text-caption">
          <v-icon x-small color="success">mdi-check</v-icon> {{ $t('firmware.engine.configuration-same', {count: update.configurationBeforeCount}) }}
        </div>
        <template v-else>
          <div class="text-caption orange--text text--darken-2 mb-1">
            {{ $t('firmware.engine.configuration-differences', {count: update.configurationDifferences.length}) }}
          </div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ $t('firmware.engine.key') }}</th>
                <th>{{ $t('firmware.engine.before') }}</th>
                <th>{{ $t('firmware.engine.after') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="difference in update.configurationDifferences" :key="difference.key">
                <td class="mono">{{ difference.key }}</td>
                <td class="mono">{{ difference.before === null ? '—' : difference.before }}</td>
                <td class="mono">{{ difference.after === null ? '—' : difference.after }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>
      </div>

      <v-expansion-panels flat class="mt-2">
        <v-expansion-panel>
          <v-expansion-panel-header class="px-0 text-caption">{{ $t('firmware.engine.log') }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-for="(entry, index) in update.log" :key="index" class="text-caption">
              <span class="text--secondary">{{ formatDate(entry.at, 'DD/MM/YYYY HH:mm:ss') }}</span>
              — {{ $t('firmware.engine.events.' + entry.what) }}
              <span v-if="entry.detail" class="mono">{{ entry.detail }}</span>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <v-dialog v-model="retryDialog" max-width="480">
      <v-card>
        <v-card-title>{{ $t('firmware.engine.retry-title') }}</v-card-title>
        <v-card-text>
          <p class="text-body-2">{{ $t('firmware.engine.retry-forced') }}</p>
          <v-textarea v-model="retryReason" :label="$t('firmware.engine.retry-reason')" rows="2" auto-grow outlined dense autofocus />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="retryDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!retryReason.trim()" :loading="retrying" @click="relaunch(retryReason.trim())">{{ $t('firmware.engine.retry') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="480">
      <v-card>
        <v-card-title>{{ $t('firmware.engine.cancel-title') }}</v-card-title>
        <v-card-text>
          <p class="text-body-2">{{ $t('firmware.engine.cancel-warning') }}</p>
          <v-textarea v-model="cancelReason" :label="$t('firmware.engine.cancel-reason')" rows="2" auto-grow outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="cancelDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="error" :loading="cancelling" @click="cancel">{{ $t('firmware.engine.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";

/** La mise à jour d'une borne par le moteur : son chemin, où elle en est, pourquoi elle attend ou a échoué. */
export default {
  mixins: [FirmwareMixin],

  props: {
    update: {type: Object, required: true},
    chargingPointId: {type: String, required: true},
    canDeploy: {type: Boolean, default: false},
    // Relancer une mise à jour forcée, c'est forcer encore : le droit dédié (le serveur le vérifie de toute façon).
    canForce: {type: Boolean, default: false},
    statusLabel: {type: Function, required: true},
    // Le statut actuel du firmware visé : en essai, relancer c'est forcer, avec un motif.
    targetStatus: {type: String, default: null},
  },

  data() {
    return {
      cancelDialog: false,
      cancelReason: '',
      cancelling: false,
      retrying: false,
      retryDialog: false,
      retryReason: '',
    }
  },

  computed: {
    currentStep() {
      return this.update.steps[this.update.currentStep - 1] || null
    },
    // Forcer de nouveau : la mise à jour l'était, ou son firmware est (re)passé en essai depuis.
    retryForces() {
      return this.update.forced || this.targetStatus === 'testing'
    },
    canRetry() {
      return this.canDeploy && ['failed', 'cancelled'].includes(this.update.phase) && (!this.retryForces || this.canForce)
    },
    phaseColor() {
      return {succeeded: 'success', failed: 'error', cancelled: 'grey', waiting: 'blue-grey'}[this.update.phase] || 'primary'
    },
  },

  methods: {
    // Une étape en cours d'une mise à jour arrêtée (ou finie) ne tourne plus : elle est dite arrêtée.
    stepState(step) {
      return step.status === 'running' && !this.update.open ? 'stopped' : step.status
    },
    stepColor(step, index) {
      const state = this.stepState(step)
      if (state === 'succeeded') return 'success'
      if (state === 'failed') return 'error'
      if (state === 'running' || (this.update.open && index === this.update.currentStep - 1)) return 'primary'
      return 'grey'
    },

    // Forcer demande un motif : la fenêtre le demande (celui d'avant proposé) ; sinon on relance tout de suite.
    retry() {
      if (this.retryForces) {
        this.retryReason = this.update.reason || ''
        this.retryDialog = true
        return
      }
      this.relaunch(null)
    },

    relaunch(reason) {
      this.retrying = true
      FirmwareRepository.startUpdate(this.chargingPointId, this.update.targetVersionId, reason).then(() => {
        this.retryDialog = false
        this.notifySuccess('firmware.engine.retried')
        this.$emit('changed')
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.retrying = false
      })
    },

    cancel() {
      this.cancelling = true
      FirmwareRepository.cancelUpdate(this.update.id, this.cancelReason.trim() || null).then(() => {
        this.cancelDialog = false
        this.cancelReason = ''
        this.$emit('changed')
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.cancelling = false
      })
    },
  },
}
</script>

<style scoped lang="scss">
.firmware-update-card {
  .mono {
    font-family: monospace;
    font-size: 12px;
    word-break: break-all;
  }
}
</style>
